import React from 'react';
import { formatNumericHumanDate } from '~/DateTime';
import Icon from '~/UI/Icon';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { Typography } from '~/libs/ui/atoms/Typography';
import { Select } from '~/libs/ui/atoms/Select';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';
import { Input } from '~/libs/ui/atoms/Input';
import { ProductSaveIndicator } from '~/libs/ui/atoms/ProductSaveIndicator';

export enum Status {
	saving = 'saving',
	saved = 'saved',
	failed = 'failed',
}

export interface Value {
	value: string;
	isValid: boolean | null;
}

export interface ReferenceValues {
	packageSize: Value;
	basketVersion: string | null;
}

export interface ProductParametersPanelProps {
	values: ReferenceValues;
	versions?: ReferenceBasketVersion[] | null;
	updateStatus: Status | null;
	onChange(values: ReferenceValues): void;
	onSave(): void;
}

export function ProductParametersPanel({
	values,
	versions,
	updateStatus,
	onChange,
	onSave,
}: ProductParametersPanelProps) {
	const currentVersion = versions?.find((version) => version.id === values.basketVersion) ?? null;

	return (
		<div className='w-full border-t border-b border-gray-200 pt-3 pb-4 bg-cBlue-50' data-testid='parameters-panel'>
			<div className='max-w-[1260px] mx-auto px-[40px]'>
				<Typography level={4} color='text-gray-800' weight='font-medium'>
					Maximum price calculation parameters:
				</Typography>
				<div className='flex items-start justify-start flex-wrap gap-x-6 gap-y-4 mt-2'>
					<label className='block w-48' htmlFor='reference-basket-version'>
						<span className='block mb-1 text-[13px] text-gray-700 tracking-wide'>Reference basket valid:</span>
						<div className='w-full inline-flex items-center justify-start'>
							<Select
								id='reference-basket-version'
								name='reference-basket-version'
								disabled={versions === null}
								value={values.basketVersion ?? ''}
								options={versions?.map((version) => ({
									value: version.id,
									key: String(version.id),
									title: `Since ${formatNumericHumanDate(new Date(version.sinceDate))}`,
								}))}
								onChange={(value) => {
									onChange({ ...values, basketVersion: value });
								}}
							/>

							{currentVersion?.description && (
								<TailwindTooltip
									reference={<Icon name='violet-question-mark' />}
									displayClass='inline-block ml-2 h-4 align-middle'
									position='top'
								>
									{currentVersion.description}
								</TailwindTooltip>
							)}
						</div>
					</label>

					<label className='block w-24' htmlFor='reference-package-size'>
						<span className='block mb-1 text-[13px] text-gray-700 tracking-wide'>Package size:</span>
						<Input
							id='reference-package-size'
							name='reference-package-size'
							value={values.packageSize.value}
							isInvalid={values.packageSize.isValid === false}
							onBlur={onSave}
							onChange={(value) => onChange({ ...values, packageSize: { value, isValid: null } })}
						/>
					</label>
				</div>

				<ProductSaveIndicator hideAfterMilliseconds={3000} status={updateStatus} />
			</div>
		</div>
	);
}
